import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, ArrowUpRight, Building2 } from 'lucide-react';

const cases = [
  {
    company: 'FinCore Bank',
    sector: 'BFSI',
    result: '99.999%',
    metric: 'Uptime achieved',
    story: 'Migrated 12+ core banking apps to DC10G’s Tier-III cloud with zero downtime and 3× faster transaction processing.',
    gradient: 'from-cyan-500 to-blue-600'
  },
  {
    company: 'MedSync Health',
    sector: 'Healthcare',
    result: '65%',
    metric: 'Cost reduction',
    story: 'Consolidated 4 legacy datacenters into DC10G colocation while meeting HIPAA compliance and doubling capacity.',
    gradient: 'from-rose-500 to-pink-600'
  },
  {
    company: 'ShopKart',
    sector: 'E-commerce',
    result: '18M+',
    metric: 'Peak orders/day',
    story: 'Scaled to handle festive traffic 20× baseline load using auto-scaling cloud servers with zero customer complaints.',
    gradient: 'from-amber-500 to-orange-600'
  }
];

const CaseStudies = () => {
  return (
    <section className="section-padding section-dark">
      <div className="container-x relative">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12 gap-6">
          <div>
            <div className="eyebrow mb-4"><span className="w-1.5 h-1.5 rounded-full bg-cyan-400" /> Success Stories</div>
            <h2 className="section-title">Real results.<br /><span className="gradient-text">Real customers.</span></h2>
          </div>
          <p className="max-w-md text-slate-400">See how leading brands transformed their operations with DC10G’s enterprise infrastructure.</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {cases.map((c, i) => (
            <motion.div
              key={c.company}
              initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.12 }}
              whileHover={{ y: -8 }}
              className="group relative p-8 rounded-3xl glass hover:border-cyan-400/40 transition-all overflow-hidden"
            >
              <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${c.gradient}`} />
              <div className={`absolute -bottom-16 -right-16 w-40 h-40 rounded-full bg-gradient-to-br ${c.gradient} opacity-20 blur-3xl group-hover:opacity-40 transition-opacity`} />

              <div className="relative">
                <div className="flex items-center justify-between mb-6">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${c.gradient} flex items-center justify-center shadow-md`}>
                    <Building2 className="text-white" size={22} />
                  </div>
                  <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-white/5 text-slate-300 uppercase tracking-wider border border-white/10">{c.sector}</span>
                </div>

                <h3 className="text-xl font-bold text-white">{c.company}</h3>
                <p className="mt-4 text-sm text-slate-400 leading-relaxed">{c.story}</p>

                <div className="mt-6 pt-6 border-t border-white/10 flex items-end justify-between">
                  <div>
                    <div className={`text-3xl font-black bg-gradient-to-r ${c.gradient} bg-clip-text text-transparent`}>{c.result}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{c.metric}</div>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-white/5 border border-white/10 group-hover:bg-cyan-500 group-hover:text-white text-slate-300 flex items-center justify-center transition-all group-hover:rotate-45">
                    <ArrowUpRight size={18} />
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-10 flex items-center justify-center gap-2 text-sm text-slate-400">
          <TrendingUp size={16} className="text-cyan-400" />
          Trusted by 500+ enterprises across BFSI, Healthcare, E-commerce &amp; more
        </div>
      </div>
    </section>
  );
};

export default CaseStudies;
